import React, { useMemo } from 'react';
import type {
  AgenticExecutionMetadata,
  AgenticToolCallLogEntry,
} from '@shared/agentic-execution';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  formatTraceDuration,
  formatTraceCurrency,
  sanitizeTraceContent,
} from './ExecutionTrace';

type ToolCallTimelineProps = {
  toolCalls: AgenticToolCallLogEntry[];
  tokenUsage?: AgenticExecutionMetadata['tokenUsage'];
  cost?: AgenticExecutionMetadata['cost'];
  showCostSummary?: boolean;
};

const statusBadgeStyles: Record<
  NonNullable<AgenticToolCallLogEntry['status']>,
  string
> = {
  success: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  error: 'bg-red-50 text-red-700 border border-red-100',
  pending: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const ToolCallTimeline: React.FC<ToolCallTimelineProps> = ({
  toolCalls,
  tokenUsage,
  cost,
  showCostSummary = false,
}) => {
  const sortedCalls = useMemo(() => {
    return [...toolCalls].sort((a, b) => {
      const aTime = Date.parse(a.startedAt ?? '') || 0;
      const bTime = Date.parse(b.startedAt ?? '') || 0;
      return aTime - bTime;
    });
  }, [toolCalls]);

  const renderTimestamp = (value?: string) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return value;
    }
  };

  const renderTokenSummary = () => {
    if (!showCostSummary) {
      return null;
    }
    if (!tokenUsage && !cost) {
      return null;
    }
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Token & cost summary
        </p>
        <div className="mt-2 grid gap-3 sm:grid-cols-3">
          {tokenUsage ? (
            <>
              {typeof tokenUsage.inputTokens === 'number' && (
                <div>
                  <p className="text-xs text-slate-500">Input tokens</p>
                  <p className="font-semibold">
                    {tokenUsage.inputTokens.toLocaleString()}
                  </p>
                </div>
              )}
              {typeof tokenUsage.outputTokens === 'number' && (
                <div>
                  <p className="text-xs text-slate-500">Output tokens</p>
                  <p className="font-semibold">
                    {tokenUsage.outputTokens.toLocaleString()}
                  </p>
                </div>
              )}
              {typeof tokenUsage.totalTokens === 'number' && (
                <div>
                  <p className="text-xs text-slate-500">Total tokens</p>
                  <p className="font-semibold">
                    {tokenUsage.totalTokens.toLocaleString()}
                  </p>
                </div>
              )}
            </>
          ) : null}
          {cost ? (
            <div className="sm:col-span-3">
              <p className="text-xs text-slate-500">
                Estimated spend ({cost.currency})
              </p>
              <p className="font-semibold">
                {formatTraceCurrency(cost.total, cost.currency)}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <Card className="border border-muted shadow-sm">
      <div className="border-b px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Execution details
            </h3>
            <p className="text-xs text-muted-foreground">
              Snapshot of every tool the orchestrator invoked for this turn.
            </p>
          </div>
          <Badge variant="secondary" className="text-xs">
            {toolCalls.length} step{toolCalls.length === 1 ? '' : 's'}
          </Badge>
        </div>
      </div>
      <div className="space-y-4 p-4">
        {renderTokenSummary()}
        {sortedCalls.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Haiku tool executions were recorded for this message.
          </p>
        ) : (
          <div className="space-y-3">
            {sortedCalls.map((call) => (
              <div
                key={call.id}
                className="rounded-lg border border-border bg-background p-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {call.tool}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTraceDuration(call.durationMs)} • Started{' '}
                      {renderTimestamp(call.startedAt)}
                    </p>
                  </div>
                  <Badge
                    className={statusBadgeStyles[call.status] ?? ''}
                    variant="outline"
                  >
                    {call.status === 'success'
                      ? 'Success'
                      : call.status === 'error'
                        ? 'Error'
                        : 'Pending'}
                  </Badge>
                </div>
                {call.resultSnippet ? (
                  <p className="mt-2 text-sm text-foreground/80">
                    {call.resultSnippet}
                  </p>
                ) : null}
                {call.error ? (
                  <p className="mt-2 text-sm text-red-600">
                    Error: {call.error}
                  </p>
                ) : null}
                {call.parameters ? (
                  <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-muted/70 p-2 text-xs text-muted-foreground">
                    {sanitizeTraceContent(call.parameters)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ToolCallTimeline;

